import { FieldTypes } from '../../helpers/constants';
import { setIn } from '../../helpers/helpers';
import { getFieldKeyPathById } from '../selectors';
import { TemplateEditorPayload, TemplateState } from '../templateeditor-types';
import { addFieldPositionReturnResult, updateFieldName } from '../updaters';
import { AiTemplateBuilderPayload, DetectedField, FieldSuggestion } from './templateeditor-ai-types';
import { createCheckboxGroup } from '../components/PropertiesPane/CheckboxManager/updaters';
import { TemplateField } from '../../types/template-types';

export function applyFieldSuggestions(state: TemplateState, payload: AiTemplateBuilderPayload): TemplateState {
  const { fieldSuggestion } = payload;
  const { field } = fieldSuggestion;

  const fieldId = placeFieldOnTemplate(state, fieldSuggestion);
  applySuggestedFieldName(state, fieldId, field);

  return state;
}

export function applyCheckboxFieldGrouping(state: TemplateState): TemplateState {
  const checkboxGroupFields = state.template.fields.filter((field) => field.isAiPlaced && !!field.aiCheckboxGroupNumber);

  // Group checkbox fields by their group number
  const checkboxGroupMap = new Map<number, TemplateField[]>();
  checkboxGroupFields.forEach((field) => {
    const groupNumber = field.aiCheckboxGroupNumber;
    if (!checkboxGroupMap.has(groupNumber)) {
      checkboxGroupMap.set(groupNumber, []);
    }
    checkboxGroupMap.get(groupNumber).push(field);
  });

  // Track used group names to generate unique names
  const usedFieldNames = new Set<string>();
  state.template.fields.forEach((field) => {
    usedFieldNames.add(field.name.toLowerCase());
  });

  checkboxGroupMap.forEach((fields) => {
    if (fields.length < 2) {
      return;
    }

    const baseName = fields[0].name.replace(/\s*\d+$/, '').trim();
    const baseGroupName = `${baseName} Group`;
    let groupName = baseGroupName;
    let counter = 1;

    while (usedFieldNames.has(groupName.toLowerCase())) {
      groupName = `${baseGroupName} ${counter}`;
      counter++;
    }

    usedFieldNames.add(groupName.toLowerCase());

    const payload: TemplateEditorPayload = {
      keyPaths: fields.map((field) => getFieldKeyPathById(state, field.id)),
      name: groupName
    };

    state = createCheckboxGroup(state, payload);
  });

  return state;
}

// PRIVATES

function placeFieldOnTemplate(state: TemplateState, fieldSuggestion: FieldSuggestion): string {
  const { field, features, rawDateFormat, pageKeyPath } = fieldSuggestion;
  const { x, y, width } = field.fieldBoundingBox;
  const { fieldInputType } = field;

  const height = field.fieldBoundingBox.height;

  const addFieldPositionPayload: TemplateEditorPayload = {
    pageKeyPath,
    type: fieldInputType,
    x,
    y,
    width,
    height,
    rawDateFormat,
    features,
    checkBoxGroupItemId: undefined,
    field: undefined,
    tableFieldProperties: {
      rowCount: field.tableFieldProperties?.rowCount,
      columns: field.tableFieldProperties?.columns?.map((column) => ({
        name: column.label,
        type: column.fieldInputType,
        width: column.width,
        transparent: column.isTransparent
      }))
    },
    // to handle the case where a field is placed on the template by ai, mark it as field committed so
    // we don't show the placeholder message
    isFieldCommitted: true,
    isAiPlaced: true,
    aiCheckboxGroupNumber: fieldInputType === FieldTypes.CheckBox ? field.checkboxGroupNumber : undefined
  };

  const { fieldId } = addFieldPositionReturnResult(state, addFieldPositionPayload);

  return fieldId;
}

function applySuggestedFieldName(state: TemplateState, fieldId: string, field: DetectedField) {
  // with the keypath of the newly added field, apply the field name
  // look through the state to see if there are any fields with that name already, if there are, append a number to the end
  // replace all trailing non-alphanumeric characters
  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const templateSafeFieldName = getTemplateSafeName(field.label, state.template.fields);

  updateFieldName(state, { keyPath: [...fieldKeyPath, 'name'], value: templateSafeFieldName });

  // if this is a table then we have to go through and name the columns as well
  if (field.fieldInputType == FieldTypes.Table) {
    field.tableFieldProperties?.columns?.forEach((column, index) => {
      const columnsKeyPath = [...fieldKeyPath, 'columns', index];
      const templateSafeColumnName = getTemplateSafeName(column.label, state.template.fields);

      setIn(state, [...columnsKeyPath, 'name'], templateSafeColumnName);
    });
  }
}

function getTemplateSafeName(label: string, templateFields: TemplateField[]): string {
  // make a set of all existing used names in the template fields
  // todo - should probably make a selector for this
  // todo - should this be kept in redux? could be useful and better for performance, but space and state bloat are concerns
  const usedNames = new Set(templateFields.map((f) => f.name.toLowerCase()));
  // remove all trailing non-alphanumeric characters
  const labelWithoutTrailingChars = label.replace(/[^\w\s]+$/g, '').trim();
  let suggestedName = labelWithoutTrailingChars;
  let counter = 1;

  // while suggestedName is already in use, append a number to the end and keep checking until we have a unique name
  while (usedNames.has(suggestedName.toLowerCase())) {
    suggestedName = `${labelWithoutTrailingChars} ${counter}`;
    counter++;
  }

  return suggestedName;
}
